import { _, helpers, mui, React, ts, ui } from '_core';

import { transform } from './helpers';
import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';
import { prepareBarChartBucketedNames, prepareBarChartCountNames } from '../../common/utils/get-series-name';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  returns: string;
  fullScreen: boolean;
  params: Params;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, returns, fullScreen, params, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<'performance' | 'count' | 'volatility' | 'information_ratio'>(
    'performance'
  );

  const loadData = async (tab: 'performance' | 'count' | 'volatility' | 'information_ratio') => {
    try {
      const response = await loadWidgetData({
        data: {
          file: returns,
          query: ['tab', 'equals', tab],
        },
      });

      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (returns) loadData(selectedTab);
    return () => setData(null);
  }, [returns, selectedTab]);

  const getTabName = (t = selectedTab) => {
    if (t == 'count') return 'Avg Count';
    return _.startCase(t);
  };

  const getTabs = () => {
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(e, newTab) => setSelectedTab(newTab)}
        aria-label="Tabs"
      >
        {['performance', 'count', 'volatility', 'information_ratio'].map((tab) => (
          <mui.core.Tab key={tab} label={getTabName(tab as any)} value={tab} />
        ))}
      </mui.core.Tabs>
    );
  };

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    const nestedBarData = nestedBarChartFormat(transform(data));
    return {
      data: prepareBarChartBucketedNames(
        nestedBarData.data,
        params.discrete ? ts.enums.BUCKET_TYPES_ENUM.FIXED_FLOAT : ts.enums.BUCKET_TYPES_ENUM.QUANTILES,
        params.quantiles
      ),
      count: prepareBarChartCountNames(
        nestedBarData.count,
        params.discrete ? ts.enums.BUCKET_TYPES_ENUM.FIXED_FLOAT : ts.enums.BUCKET_TYPES_ENUM.QUANTILES,
        params.quantiles
      ),
    };
  }, [data, params.discrete, params.quantiles]);

  const alert = error
    ? { type: 'error', message: helpers.parseApiError(error) }
    : data?.length === 0
      ? { type: 'warning', message: 'Empty File' }
      : undefined;

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>
        {!_.isNil(formattedData) && (
          <ui.NestedBarChart
            id={id}
            data={formattedData?.data}
            count={formattedData?.count}
            category="category"
            numberFormat={selectedTab == 'performance' || selectedTab == 'volatility' ? '#.##%' : '#.##'}
            alert={alert as any}
            fullScreen={fullScreen}
            exportTitle={title}
            readOnly={readOnly}
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
